import type {
  BrowserCanvasRead,
  BrowserCanvasPatch,
  BrowserCanvasQuery,
  BrowserCanvasProperties,
} from "@excalidraw-ms/shared";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import { convertToExcalidrawElements, getCommonBounds } from "@excalidraw/excalidraw";
import type { ToolContext } from "./types";
import { commitCanvas, mutationError } from "./canvasTransaction";
import { canvasProperties, drawingPatchSchema, drawingReadSchema } from "./canvasSchemas";
import { CanvasInputError, jsonBytes, toolError, validateInput } from "./inputValidation";

export const CANVAS_LIMITS = {
  ttlMs: 5 * 60 * 1000,
  observations: 8,
  receipts: 128,
  sceneElements: 10000,
  sceneBytes: 8 * 1024 * 1024,
  pageBytes: 64 * 1024,
  affected: 200,
} as const;
type RecordElement = Record<string, unknown> & { id: string; type: string };
type Observation = {
  created: number;
  baseline: Map<string, string>;
  scene: RecordElement[];
  pages: Record<string, unknown>[];
};
type Receipt = { created: number; request: string; result: Record<string, unknown> };

function fail(code: string, message: string, field?: string): never {
  throw new CanvasInputError(code, message, field);
}

function nativeProperties(previous: Record<string, unknown>, properties: BrowserCanvasProperties) {
  const { textStyle, ...native } = properties;
  if (!textStyle) return native;
  const customData = { ...(previous.customData as Record<string, unknown> | undefined) };
  const style = { ...(customData.textStyle as Record<string, unknown> | undefined) };
  for (const key of ["bold", "italic"] as const) {
    if (textStyle[key] === true) style[key] = true;
    else if (textStyle[key] === false) delete style[key];
  }
  if (Object.keys(style).length) customData.textStyle = style;
  else delete customData.textStyle;
  return { ...native, customData: Object.keys(customData).length ? customData : undefined };
}

function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) => {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      return Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)));
    }
    return item;
  });
}

function project(element: RecordElement): RecordElement {
  const result: RecordElement = { id: element.id, type: element.type };
  for (const key of Object.keys(canvasProperties)) {
    if (element[key] !== undefined) result[key] = element[key];
  }
  for (const key of ["startBinding", "endBinding"]) {
    const binding = element[key] as {
      elementId: string;
      focus: number;
      gap: number;
      fixedPoint?: number[] | null;
    } | null;
    if (binding)
      result[key] = {
        elementId: binding.elementId,
        focus: binding.focus,
        gap: binding.gap,
        ...(binding.fixedPoint !== undefined ? { fixedPoint: binding.fixedPoint } : {}),
      };
  }
  if (Array.isArray(element.boundElements)) {
    result.boundElements = element.boundElements.map(({ id, type }) => ({ id, type }));
  }
  // No arbitrary customData, links, image payloads, file IDs, revisions or scene metadata.
  const textStyle = (element.customData as { textStyle?: unknown } | undefined)?.textStyle;
  if (element.type === "text") {
    const { bold, italic } = (textStyle ?? {}) as { bold?: boolean; italic?: boolean };
    result.textStyle = { bold: bold === true, italic: italic === true };
  }
  return result;
}

function matches(element: RecordElement, query: BrowserCanvasQuery, scene: RecordElement[]) {
  if (query.region) {
    const [left, top, right, bottom] = getCommonBounds([element as unknown as ExcalidrawElement]);
    const { x, y, width, height } = query.region;
    if (right < x || bottom < y || left > x + width || top > y + height) return false;
  }
  if (query.types && !query.types.includes(element.type)) return false;
  for (const key of ["strokeColor", "backgroundColor"] as const) {
    if (query[key] && String(element[key]).toLowerCase() !== query[key]!.toLowerCase())
      return false;
  }
  if (query.frameId && element.frameId !== query.frameId) return false;
  if (query.groupId && !(element.groupIds as string[] | undefined)?.includes(query.groupId))
    return false;
  if (query.text) {
    const text = [
      element.text,
      ...scene.filter((e) => e.containerId === element.id).map((e) => e.text),
    ]
      .filter((value) => typeof value === "string")
      .join("\n");
    if (!text.toLowerCase().includes(query.text.toLowerCase())) return false;
  }
  return true;
}

function references(element: RecordElement): string[] {
  return [
    element.frameId,
    element.containerId,
    (element.startBinding as { elementId?: string } | null)?.elementId,
    (element.endBinding as { elementId?: string } | null)?.elementId,
    ...((element.boundElements as { id: string }[] | null) ?? []).map((e) => e.id),
  ].filter((id): id is string => typeof id === "string");
}

function dependencies(scene: readonly RecordElement[], targets: Set<string>): Set<string> {
  const result = new Set(targets);
  const groups = new Set(
    scene.filter((e) => targets.has(e.id)).flatMap((e) => (e.groupIds as string[]) ?? []),
  );
  for (const element of scene) {
    if (targets.has(element.id)) for (const ref of references(element)) result.add(ref);
    if (
      references(element).some((id) => targets.has(id)) ||
      ((element.groupIds as string[]) ?? []).some((id) => groups.has(id))
    )
      result.add(element.id);
  }
  return result;
}

function validateProperties(type: string, properties: BrowserCanvasProperties, field: string) {
  const record = properties as Record<string, unknown>;
  const textKeys = [
    "textStyle",
    "text",
    "fontSize",
    "fontFamily",
    "textAlign",
    "verticalAlign",
    "containerId",
  ];
  const linearKeys = ["points", "startBinding", "endBinding", "startArrowhead", "endArrowhead"];
  if (textKeys.some((key) => Object.hasOwn(record, key)) && type !== "text") {
    fail("INVALID_INPUT", "Text properties require a text element.", field);
  }
  if (linearKeys.some((key) => Object.hasOwn(record, key)) && !["arrow", "line"].includes(type)) {
    fail("INVALID_INPUT", "Line properties require an arrow or line.", field);
  }
  if (Object.hasOwn(record, "name") && !["frame", "magicframe"].includes(type)) {
    fail("INVALID_INPUT", "Names require a frame.", field);
  }
  if (
    type === "line" &&
    ["startBinding", "endBinding", "startArrowhead", "endArrowhead"].some((key) =>
      Object.hasOwn(record, key),
    )
  ) {
    fail("INVALID_INPUT", "Bindings and arrowheads require an arrow.", field);
  }
  if (properties.groupIds && new Set(properties.groupIds).size !== properties.groupIds.length) {
    fail("INVALID_INPUT", "Group IDs must be unique.", field);
  }
  for (const key of ["strokeColor", "backgroundColor"] as const) {
    if (
      properties[key] &&
      !/^(#[\da-f]{3,8}|[a-z]{1,32}|rgba?\([\d.,% ]+\)|hsla?\([\d.,% ]+\))$/i.test(properties[key]!)
    ) {
      fail("INVALID_INPUT", "Use a CSS color, not a URL or expression.", `${field}.${key}`);
    }
    if (properties[key]) {
      const style = document.createElement("span").style;
      style.color = properties[key]!;
      if (!style.color)
        fail("INVALID_INPUT", "The color is not a valid CSS color.", `${field}.${key}`);
    }
  }
  if (properties.points && (properties.points[0][0] !== 0 || properties.points[0][1] !== 0)) {
    fail("INVALID_INPUT", "Line points must start at [0,0].", `${field}.points`);
  }
}

/** One instance per open canvas lifetime; no storage/network access and no async commit gap. */
export class CanvasTools {
  private observations = new Map<string, Observation>();
  private cursors = new Map<string, { token: string; page: number }>();
  private receipts = new Map<string, Receipt>();
  private disposed = false;
  private scope?: { api: ToolContext["excalidrawAPI"]; drawingId: string };

  constructor(
    private readonly context: () => ToolContext,
    private readonly now = Date.now,
  ) {}

  dispose() {
    this.disposed = true;
    this.observations.clear();
    this.cursors.clear();
    this.receipts.clear();
  }

  private ready(signal?: AbortSignal) {
    if (this.disposed || signal?.aborted) fail("ABORTED", "The canvas handle is no longer active.");
    const ctx = this.context();
    if (!ctx.excalidrawAPI) fail("NOT_READY", "No canvas is ready.");
    if (
      this.scope &&
      (this.scope.api !== ctx.excalidrawAPI || this.scope.drawingId !== ctx.drawingId)
    ) {
      this.observations.clear();
      this.cursors.clear();
      this.receipts.clear();
    }
    this.scope = { api: ctx.excalidrawAPI, drawingId: ctx.drawingId };
    this.prune();
    return ctx;
  }

  private prune() {
    for (const [token, entry] of this.observations) {
      if (this.now() - entry.created >= CANVAS_LIMITS.ttlMs) this.observations.delete(token);
    }
    for (const [cursor, entry] of this.cursors) {
      if (!this.observations.has(entry.token)) this.cursors.delete(cursor);
    }
    for (const [id, entry] of this.receipts) {
      if (this.now() - entry.created >= CANVAS_LIMITS.ttlMs) this.receipts.delete(id);
    }
  }

  private observation(token: string) {
    const observation = this.observations.get(token);
    if (!observation)
      fail("READ_REQUIRED", "The read token is unknown, expired or from another canvas.");
    return observation;
  }

  read(input: unknown, signal?: AbortSignal): string {
    try {
      validateInput(input, drawingReadSchema);
      const request = input as BrowserCanvasRead;
      const ctx = this.ready(signal);
      if (request.cursor) {
        if (Object.keys(request).length !== 1)
          fail("INVALID_INPUT", "A cursor must be the only input field.");
        const cursor = this.cursors.get(request.cursor);
        if (!cursor) fail("READ_REQUIRED", "The read cursor has expired or is unknown.");
        return JSON.stringify(this.observation(cursor.token).pages[cursor.page]);
      }
      const mode = request.mode ?? "all";
      if (
        (mode === "ids") !== !!request.ids ||
        (mode === "query") !== !!request.query ||
        (mode === "changes") !== !!request.readToken
      ) {
        fail(
          "INVALID_INPUT",
          "Use ids only with ids mode, query only with query mode, and readToken only with changes mode.",
        );
      }
      if (request.query && !Object.keys(request.query).length)
        fail("INVALID_INPUT", "Query filters must not be empty.");
      const api = ctx.excalidrawAPI!;
      const elements = api.getSceneElementsIncludingDeleted();
      if (
        elements.length > CANVAS_LIMITS.sceneElements ||
        jsonBytes(elements) > CANVAS_LIMITS.sceneBytes
      ) {
        fail("LIMIT_EXCEEDED", "This canvas exceeds the supported observation budget.");
      }
      const scene = JSON.parse(JSON.stringify(elements)) as RecordElement[];
      const baseline = new Map(scene.map((element) => [element.id, canonical(element)]));
      const visible = scene.filter((element) => !element.isDeleted);
      const state = api.getAppState();
      const selectedIds = Object.keys(state.selectedElementIds ?? {}).filter(
        (id) => state.selectedElementIds[id],
      );
      let selected = visible;
      let deletedIds: string[] = [];
      if (mode === "ids") selected = visible.filter((e) => request.ids!.includes(e.id));
      if (mode === "query") selected = visible.filter((e) => matches(e, request.query!, visible));
      if (mode === "selection") selected = visible.filter((e) => selectedIds.includes(e.id));
      if (mode === "overview") selected = [];
      if (mode === "changes") {
        const old = this.observation(request.readToken!);
        selected = visible.filter((e) => old.baseline.get(e.id) !== baseline.get(e.id));
        const live = new Set(visible.map((e) => e.id));
        deletedIds = old.scene.filter((e) => !e.isDeleted && !live.has(e.id)).map((e) => e.id);
      }
      const token = crypto.randomUUID();
      const counts: Record<string, number> = {};
      for (const element of visible) counts[element.type] = (counts[element.type] ?? 0) + 1;
      const metadata = {
        ok: true,
        readToken: token,
        mode,
        scope: ["ids", "query", "selection"].includes(mode)
          ? "subset"
          : mode === "changes"
            ? "delta"
            : mode === "overview"
              ? "overview"
              : "scene",
        editable: !mutationError(ctx),
        totalElements: visible.length,
        matchedElements: selected.length,
        deletedCount: deletedIds.length,
        counts,
        selection: {
          ids: selectedIds.slice(0, 100),
          total: selectedIds.length,
          complete: selectedIds.length <= 100,
        },
        viewport: {
          scrollX: state.scrollX,
          scrollY: state.scrollY,
          width: state.width,
          height: state.height,
          zoom: state.zoom?.value,
        },
      };
      const entries = [
        ...selected.map((e) => ({ element: project(e) })),
        ...deletedIds.map((id) => ({ deletedId: id })),
      ];
      const pages: Record<string, unknown>[] = [];
      let offset = 0;
      let pageBytes = 0;
      do {
        const pageElements: RecordElement[] = [];
        const pageDeleted: string[] = [];
        const start = offset;
        while (offset < entries.length && offset - start < (request.pageSize ?? 50)) {
          const entry = entries[offset];
          if ("element" in entry) pageElements.push(entry.element);
          else pageDeleted.push(entry.deletedId);
          if (
            jsonBytes({ ...metadata, elements: pageElements, deletedIds: pageDeleted }) >
            CANVAS_LIMITS.pageBytes - 512
          ) {
            if ("element" in entry) pageElements.pop();
            else pageDeleted.pop();
            if (offset === start)
              fail("LIMIT_EXCEEDED", "An element exceeds the output page budget.");
            break;
          }
          offset++;
        }
        pages.push({
          ...metadata,
          elements: pageElements,
          deletedIds: pageDeleted,
          complete: offset === entries.length,
          partial: offset < entries.length,
        });
        pageBytes += jsonBytes(pages[pages.length - 1]) + 128;
        if (pageBytes > CANVAS_LIMITS.sceneBytes)
          fail(
            "LIMIT_EXCEEDED",
            "The read exceeds the retained page budget. Narrow the read or increase pageSize.",
          );
      } while (offset < entries.length);
      for (let index = 1; index < pages.length; index++) {
        const cursor = crypto.randomUUID();
        this.cursors.set(cursor, { token, page: index });
        pages[index - 1].nextCursor = cursor;
      }
      this.observations.set(token, { created: this.now(), baseline, scene, pages });
      while (this.observations.size > CANVAS_LIMITS.observations)
        this.observations.delete(this.observations.keys().next().value!);
      this.prune();
      return JSON.stringify(pages[0]);
    } catch (error) {
      return JSON.stringify(toolError(error));
    }
  }

  patch(input: unknown, signal?: AbortSignal): string {
    try {
      validateInput(input, drawingPatchSchema);
      const request = JSON.parse(JSON.stringify(input)) as BrowserCanvasPatch;
      const ctx = this.ready(signal);
      if (mutationError(ctx)) fail("READ_ONLY", "This canvas cannot be edited.");
      const key = canonical(request);
      const receipt = this.receipts.get(request.operationId);
      if (receipt) {
        if (receipt.request !== key)
          fail("OPERATION_MISMATCH", "This operation ID was used with different input.");
        return JSON.stringify(receipt.result);
      }
      const observation = this.observation(request.readToken);
      const previous = ctx.excalidrawAPI!.getSceneElementsIncludingDeleted();
      const byId = new Map(previous.map((e) => [e.id, e]));
      const candidate = new Map(previous.map((e) => [e.id, e as unknown as RecordElement]));
      const targets = new Set<string>();
      request.operations.forEach((operation, index) => {
        const id = operation.op === "add" ? operation.element.id : operation.id;
        const field = `operations[${index}]`;
        if (targets.has(id))
          fail("INVALID_INPUT", "Each element may occur only once in a batch.", field);
        targets.add(id);
        if (operation.op === "add") {
          if (byId.has(id)) fail("INVALID_INPUT", "An addition must use a new unique ID.", field);
          validateProperties(operation.element.type, operation.element, field);
          if (operation.element.type === "text" && typeof operation.element.text !== "string")
            fail("INVALID_INPUT", "New text requires text.", field);
          if (["arrow", "line"].includes(operation.element.type) && !operation.element.points)
            fail("INVALID_INPUT", "New lines require points.", field);
          const normalized = convertToExcalidrawElements(
            [
              {
                ...operation.element,
                ...(operation.element.type === "frame" ? { children: [] } : {}),
              } as NonNullable<Parameters<typeof convertToExcalidrawElements>[0]>[number],
            ],
            { regenerateIds: false },
          );
          if (normalized.length !== 1)
            fail("INVALID_INPUT", "The addition could not be normalized.", field);
          candidate.set(id, {
            ...normalized[0],
            ...nativeProperties({}, operation.element),
            ...(operation.element.type === "text" ? { originalText: operation.element.text } : {}),
          });
        } else {
          const old = byId.get(id);
          if (!old || old.isDeleted)
            fail("NOT_FOUND", "The target does not exist in this canvas.", field);
          if (old.locked) fail("LOCKED", "A targeted element is locked.", field);
          if (operation.op === "delete") candidate.set(id, { ...old, isDeleted: true });
          else {
            if (!Object.keys(operation.properties).length)
              fail("INVALID_INPUT", "Updates must include properties.", field);
            validateProperties(old.type, operation.properties, field);
            candidate.set(id, {
              ...old,
              ...nativeProperties(old, operation.properties),
              ...(operation.properties.text !== undefined
                ? { originalText: operation.properties.text }
                : {}),
            });
          }
        }
      });
      this.repairRelations(candidate, targets);
      const proposed = [...candidate.values()];
      if (
        proposed.length > CANVAS_LIMITS.sceneElements ||
        jsonBytes(proposed) > CANVAS_LIMITS.sceneBytes
      ) {
        fail("LIMIT_EXCEEDED", "The resulting canvas exceeds the observation budget.");
      }
      this.validateRelations(proposed, targets);
      const current = previous as unknown as RecordElement[];
      const checkFresh = (ids: Set<string>) => {
        const related = new Set([
          ...dependencies(observation.scene, ids),
          ...dependencies(current, ids),
          ...dependencies(proposed, ids),
        ]);
        for (const id of related) {
          const old = byId.get(id);
          if (observation.baseline.get(id) !== (old ? canonical(old) : undefined)) {
            fail("STALE_READ", "A targeted or related element changed. Read again before editing.");
          }
          if (old?.locked) fail("LOCKED", "A targeted or related element is locked.");
        }
      };
      checkFresh(targets);
      let affectedIds: string[] = [];
      commitCanvas(ctx, proposed, (normalized: readonly ExcalidrawElement[]) => {
        for (const element of normalized) {
          if (
            !["x", "y", "width", "height", "angle"].every((key) =>
              Number.isFinite(element[key as keyof ExcalidrawElement]),
            )
          ) {
            fail("INVALID_INPUT", "The resulting geometry is invalid.");
          }
        }
        affectedIds = normalized
          .filter((element) => element !== byId.get(element.id))
          .map((e) => e.id);
        if (affectedIds.length > CANVAS_LIMITS.affected)
          fail("LIMIT_EXCEEDED", "The transaction affects more than 200 elements.");
        checkFresh(new Set([...targets, ...affectedIds]));
        this.validateRelations(normalized as unknown as RecordElement[], new Set(affectedIds));
        if (signal?.aborted) fail("ABORTED", "The operation was cancelled.");
      });
      const result = {
        ok: true,
        applied: true,
        operationId: request.operationId,
        affectedIds,
        persistence: "editor-managed",
      };
      this.receipts.set(request.operationId, { created: this.now(), request: key, result });
      while (this.receipts.size > CANVAS_LIMITS.receipts)
        this.receipts.delete(this.receipts.keys().next().value!);
      return JSON.stringify(result);
    } catch (error) {
      return JSON.stringify(toolError(error));
    }
  }

  private repairRelations(candidate: Map<string, RecordElement>, targets: Set<string>) {
    const deleted = (id: unknown) => typeof id === "string" && candidate.get(id)?.isDeleted;
    for (const [id, element] of candidate) {
      if (element.isDeleted) continue;
      const updates: Record<string, unknown> = {};
      if (deleted(element.containerId)) updates.isDeleted = true;
      if (deleted(element.frameId)) updates.frameId = null;
      for (const key of ["startBinding", "endBinding"] as const) {
        if (deleted((element[key] as { elementId?: string } | null)?.elementId))
          updates[key] = null;
      }
      if (Object.keys(updates).length) {
        candidate.set(id, { ...element, ...updates });
        targets.add(id);
      }
    }
    // Binding back-references are derived, never client-controlled.
    const affected = dependencies([...candidate.values()], targets);
    const referencesById = new Map<string, { id: string; type: string }[]>();
    for (const element of candidate.values()) {
      if (element.isDeleted) continue;
      const ids = new Set(
        [
          element.containerId,
          ...["startBinding", "endBinding"].map(
            (key) => (element[key] as { elementId?: string } | null)?.elementId,
          ),
        ].filter((id): id is string => typeof id === "string"),
      );
      for (const id of ids) {
        const refs = referencesById.get(id) ?? [];
        refs.push({ id: element.id, type: element.type });
        referencesById.set(id, refs);
      }
    }
    for (const [id, element] of candidate) {
      if (element.isDeleted) continue;
      if (!affected.has(id)) continue;
      const refs = referencesById.get(id) ?? [];
      const old = (element.boundElements as { id: string; type: string }[] | null) ?? [];
      if (
        canonical([...old].sort((a, b) => a.id.localeCompare(b.id))) !==
        canonical([...refs].sort((a, b) => a.id.localeCompare(b.id)))
      ) {
        candidate.set(id, { ...element, boundElements: refs.length ? refs : null });
        targets.add(id);
      }
    }
  }

  private validateRelations(scene: RecordElement[], targets: Set<string>) {
    const live = new Map(scene.filter((e) => !e.isDeleted).map((e) => [e.id, e]));
    for (const element of live.values()) {
      if (!targets.has(element.id)) continue;
      const get = (id: string) => {
        const target = live.get(id);
        if (!target || id === element.id)
          fail("INVALID_RELATIONSHIP", "A relationship needs a distinct existing element.");
        return target;
      };
      if (element.frameId) {
        if (
          !["frame", "magicframe"].includes(get(element.frameId as string).type) ||
          ["frame", "magicframe"].includes(element.type)
        ) {
          fail(
            "INVALID_RELATIONSHIP",
            "Frames cannot be nested and frameId must identify a frame.",
          );
        }
      }
      if (element.containerId) {
        const target = get(element.containerId as string);
        if (
          element.type !== "text" ||
          !["rectangle", "ellipse", "diamond", "arrow"].includes(target.type)
        ) {
          fail("INVALID_RELATIONSHIP", "Text must bind to a supported shape or arrow.");
        }
        if ([...live.values()].filter((e) => e.containerId === target.id).length > 1)
          fail("INVALID_RELATIONSHIP", "A shape may contain only one bound label.");
      }
      for (const key of ["startBinding", "endBinding"]) {
        const binding = element[key] as { elementId: string } | null;
        if (
          binding &&
          (element.type !== "arrow" ||
            !["rectangle", "ellipse", "diamond", "text", "image", "embeddable"].includes(
              get(binding.elementId).type,
            ))
        ) {
          fail("INVALID_RELATIONSHIP", "Arrow bindings require an eligible shape.");
        }
      }
    }
  }
}
